import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import FileUpload from "@/components/FileUpload";
import FilePreview from "@/components/FilePreview";
import { toast } from "sonner";
import {
  Pencil, X, Save, Plus, Trash2, FileText, Image as ImageIcon, IdCard, ShieldCheck,
  MapPin, Briefcase, Cpu, Wrench, Building2, CalendarClock, UserCircle
} from "lucide-react";
import { COUNTRIES, LANGUAGES, DEVICE_TYPES, MANUFACTURERS, OS_VERSIONS, ADDITIONAL_DEVICES, DAYS, TIME_SLOTS } from "@/lib/countries";
import { AgreementsCard } from "@/pages/profile/AgreementsCard";
import { ProfileSummaryCard, ProfileCompletionCard } from "@/pages/profile/SideWidgets";
import DeviceInventory from "@/components/DeviceInventory";
import DeviceInventoryList from "@/components/DeviceInventoryList";
import TesterTierCard from "@/components/TesterTierCard";

const SKILLS = ["functional", "exploratory", "regression", "usability", "accessibility", "API", "automation", "AI testing", "LLM evaluation", "red teaming"];
const TOOLS = ["Jira", "TestRail", "Zephyr", "Xray", "BrowserStack", "Postman", "Playwright", "Selenium", "Charles Proxy"];
const DOMAINS = ["banking", "ecommerce", "healthcare", "telecom", "SaaS", "gaming", "AI/ML", "fintech"];
const PROFICIENCY = ["Native", "Fluent", "Professional", "Conversational", "Basic"];
const ENGAGEMENT_TYPES = ["Part-time", "Full-time", "Project-based"];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [u, setU] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [editing, setEditing] = useState(null); // section key currently in edit mode
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState({ open: false, fileId: null, label: "" });

  const load = async () => {
    const { data } = await api.get("/auth/me");
    setU(data);
    if (data?.role === "freelancer") {
      try {
        const r = await api.get("/scoring/me");
        setScoreData(r.data);
      } catch { /* non-fatal */ }
    }
  };
  useEffect(() => { load(); }, []);

  if (!u) return <AppLayout title="Profile"><div className="text-slate-500">Loading…</div></AppLayout>;

  const startEdit = (section, snapshot) => { setEditing(section); setDraft({ ...snapshot }); };
  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const save = async (payload) => {
    setBusy(true);
    try {
      await api.patch("/profile", payload);
      toast.success("Saved");
      setEditing(null);
      setDraft({});
      load();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const deleteFile = async (type) => {
    try {
      await api.delete(`/profile/file?type=${type}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  return (
    <AppLayout title="My Profile" subtitle="View and manage your tester profile">
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ProfileInfoCard u={u} editing={editing===0} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(0, { name: u.name, phone: u.phone || "" })}
            onCancel={cancelEdit} onSave={()=>save({ name: draft.name, phone: draft.phone })}
            onAvatar={(f)=>save({ avatar_file_id: f.id })}
            onAvatarDelete={()=>deleteFile("avatar")}
            busy={busy} />
          <LocationLanguagesCard u={u} editing={editing===1} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(1, {
              country: u.country || "", state: u.state || "", city: u.city || "", timezone: u.timezone || "",
              languages: u.languages || [], language_proficiency: u.language_proficiency || {},
            })}
            onCancel={cancelEdit}
            onSave={()=>save({ country: draft.country, state: draft.state, city: draft.city, timezone: draft.timezone, languages: draft.languages, language_proficiency: draft.language_proficiency })}
            busy={busy} />
          <SkillsCard u={u} editing={editing===2} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(2, {
              testing_skills: u.testing_skills || [], skill_years: u.skill_years || {},
              custom_skills: u.custom_skills || "", years_experience: u.years_experience ?? 0,
            })}
            onCancel={cancelEdit}
            onSave={()=>save({ testing_skills: draft.testing_skills, skill_years: draft.skill_years, custom_skills: draft.custom_skills, years_experience: Number(draft.years_experience) })}
            busy={busy} />
          <DevicesCard u={u} editing={editing===3} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(3, {
              device_inventory: u.device_inventory || [],
              device_types: u.device_types || [], device_manufacturers: u.device_manufacturers || [],
              os_versions: u.os_versions || [], additional_devices: u.additional_devices || [],
            })}
            onCancel={cancelEdit}
            onSave={()=>save({ device_inventory: draft.device_inventory, device_types: draft.device_types, device_manufacturers: draft.device_manufacturers, os_versions: draft.os_versions, additional_devices: draft.additional_devices })}
            busy={busy} />
          <ToolsCard u={u} editing={editing===4} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(4, { tools: u.tools || [], custom_tools: u.custom_tools || "" })}
            onCancel={cancelEdit}
            onSave={()=>save({ tools: draft.tools, custom_tools: draft.custom_tools })}
            busy={busy} />
          <DomainsCard u={u} editing={editing===5} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(5, { domains: u.domains || [], custom_domains: u.custom_domains || "" })}
            onCancel={cancelEdit}
            onSave={()=>save({ domains: draft.domains, custom_domains: draft.custom_domains })}
            busy={busy} />
          <AvailabilityCard u={u} editing={editing===6} draft={draft} setDraft={setDraft}
            onEdit={()=>startEdit(6, {
              availability_hours: u.availability_hours || 0,
              working_days: u.working_days || [], availability_schedule: u.availability_schedule || [],
            })}
            onCancel={cancelEdit}
            onSave={()=>save({ availability_hours: draft.availability_hours, working_days: draft.working_days, availability_schedule: draft.availability_schedule })}
            busy={busy} />
          <AgreementsCard u={u} onUpload={(type, f)=>save({ [type]: f.id })} onDelete={(type)=>deleteFile(type)} onPreview={(fileId, label)=>setPreview({open:true, fileId, label})} />
        </div>

        <div className="space-y-4">
          {u.role === "freelancer" && (
            <TesterTierCard
              tier={scoreData?.tier || "Unrated"}
              score={scoreData?.score ?? null}
              state={scoreData?.state || "unrated"}
              completedCount={scoreData?.completed_count ?? 0}
            />
          )}
          <ProfileSummaryCard u={u} />
          <ProfileCompletionCard u={u} />
        </div>
      </div>
      <FilePreview open={preview.open} onOpenChange={(o)=>setPreview(p=>({...p, open:o}))} fileId={preview.fileId} label={preview.label}/>
    </AppLayout>
  );
}

/* ---------- Reusable section frame ---------- */
function SectionCard({ icon: Icon, title, subtitle, editing, onEdit, onCancel, onSave, busy, canEdit = true, testid, children, hideSave }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-cyan-400 flex items-center justify-center"><Icon className="w-4 h-4" strokeWidth={1.75}/></div>
          <div>
            <h3 className="font-display font-bold text-slate-900">{title}</h3>
            {subtitle && <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`${testid}-edit`}><Pencil className="w-3.5 h-3.5 mr-1"/>Edit</Button>
        )}
        {editing && !hideSave && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onCancel} data-testid={`${testid}-cancel`}><X className="w-3.5 h-3.5 mr-1"/>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid={`${testid}-save`}><Save className="w-3.5 h-3.5 mr-1"/>Save</Button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-slate-500 italic">{children}</div>;
}
function Pill({ children, onRemove, testid }) {
  return (
    <span className="pill bg-slate-100 text-slate-700 border-slate-200" data-testid={testid}>
      {children}
      {onRemove && <button onClick={onRemove} className="ml-1 text-slate-400 hover:text-red-600"><X className="w-3 h-3"/></button>}
    </span>
  );
}
function MultiToggle({ value, options, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(opt => {
        const active = value.includes(opt);
        return (
          <button type="button" key={opt}
            onClick={()=>onChange(active ? value.filter(v=>v!==opt) : [...value, opt])}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- 1. Profile Info ---------- */
function ProfileInfoCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, onAvatar, onAvatarDelete, busy }) {
  return (
    <SectionCard icon={UserCircle} title="Profile Info" subtitle="Section 1" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-profile-info">
      <div className="flex items-start gap-5">
        <div className="flex-shrink-0">
          {u.avatar_file_id ? (
            <div className="relative group">
              <img src={`${API}/files/${u.avatar_file_id}`} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" data-testid="avatar-img"/>
              <button onClick={onAvatarDelete} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="avatar-delete"><X className="w-3 h-3"/></button>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold text-2xl">{u.name?.[0] || "?"}</div>
          )}
          <div className="mt-2 w-20">
            <FileUpload accept="image/jpeg,image/png" testid="upload-avatar" label={u.avatar_file_id ? "Replace" : "Upload"} onUploaded={onAvatar}/>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-3">
          <ViewOrInput label="Full name" value={u.name} editing={editing} testid="full-name"
            input={<Input value={draft.name || ""} onChange={e=>setDraft(d=>({...d, name:e.target.value}))} data-testid="input-name"/>}/>
          <ViewOrInput label="Email" value={<span className="flex items-center gap-1">{u.email} <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200">verified</span></span>} editing={false}/>
          <ViewOrInput label="Phone" value={u.phone || <Empty>Not added</Empty>} editing={editing} testid="phone"
            input={<Input value={draft.phone || ""} onChange={e=>setDraft(d=>({...d, phone:e.target.value}))} placeholder="+1 555 0100" type="tel" inputMode="tel" autoComplete="tel" data-testid="input-phone"/>}/>
          <ViewOrInput label="Role" value={<span className="uppercase tracking-wider text-xs">{u.role}</span>} editing={false}/>
        </div>
      </div>
    </SectionCard>
  );
}

function ViewOrInput({ label, value, editing, input, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-slate-500 tracking-wider">{label}</div>
      {editing ? <div className="mt-1" data-testid={`field-${testid}`}>{input}</div> : <div className="font-medium text-slate-900 mt-0.5" data-testid={`view-${testid}`}>{value}</div>}
    </div>
  );
}

/* ---------- 2. Location & Languages ---------- */
function LocationLanguagesCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, busy }) {
  const cityOptions = ((COUNTRIES.find(([c])=>c === (draft.country || u.country)) || [null, []])[1]);
  const toggleLang = (lang) => {
    const has = (draft.languages || []).includes(lang);
    setDraft(d=>{
      const langs = has ? d.languages.filter(x=>x!==lang) : [...(d.languages||[]), lang];
      const prof = { ...(d.language_proficiency || {}) };
      if (!has && !prof[lang]) prof[lang] = "Professional";
      if (has) delete prof[lang];
      return { ...d, languages: langs, language_proficiency: prof };
    });
  };
  const setProf = (lang, p) => setDraft(d=>({ ...d, language_proficiency: { ...(d.language_proficiency||{}), [lang]: p } }));

  return (
    <SectionCard icon={MapPin} title="Location & Languages" subtitle="Section 2" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-location">
      {!editing && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <View label="Country" value={u.country || <Empty>—</Empty>}/>
            <View label="State / Region" value={u.state || <Empty>—</Empty>}/>
            <View label="City" value={u.city || <Empty>—</Empty>}/>
            <View label="Timezone" value={u.timezone || <Empty>—</Empty>}/>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2">Languages spoken</div>
            {(u.languages || []).length === 0 ? <Empty>No languages added yet. Add one to get matched.</Empty> : (
              <div className="flex flex-wrap gap-2">
                {u.languages.map(l => (
                  <Pill key={l} testid={`lang-${l}`}>{l}{u.language_proficiency?.[l] ? <span className="text-[10px] text-slate-500 ml-1">· {u.language_proficiency[l]}</span> : null}</Pill>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {editing && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Country</Label>
              <Select value={draft.country} onValueChange={v=>setDraft(d=>({...d, country: v, city: ""}))}>
                <SelectTrigger data-testid="select-country"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent className="max-h-80">{COUNTRIES.map(([c])=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>State / Region</Label><Input value={draft.state || ""} onChange={e=>setDraft(d=>({...d, state:e.target.value}))} data-testid="input-state"/></div>
            <div>
              <Label>City</Label>
              <Select value={draft.city} onValueChange={v=>setDraft(d=>({...d, city: v}))} disabled={!draft.country}>
                <SelectTrigger data-testid="select-city"><SelectValue placeholder={draft.country ? "Select" : "Country first"}/></SelectTrigger>
                <SelectContent className="max-h-80">{cityOptions.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Timezone</Label><Input value={draft.timezone || ""} onChange={e=>setDraft(d=>({...d, timezone:e.target.value}))} placeholder="UTC" data-testid="input-timezone"/></div>
          </div>
          <div>
            <Label>Languages</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {LANGUAGES.map(l => {
                const active = (draft.languages || []).includes(l);
                return (
                  <button type="button" key={l} onClick={()=>toggleLang(l)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            {(draft.languages || []).length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-[11px] uppercase text-slate-500 tracking-wider">Proficiency</div>
                {draft.languages.map(l => (
                  <div key={l} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-900 w-32 truncate">{l}</span>
                    <Select value={draft.language_proficiency?.[l] || "Professional"} onValueChange={(v)=>setProf(l, v)}>
                      <SelectTrigger className="w-48" data-testid={`select-prof-${l}`}><SelectValue/></SelectTrigger>
                      <SelectContent>{PROFICIENCY.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- 3. Skills ---------- */
function SkillsCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, busy }) {
  return (
    <SectionCard icon={Briefcase} title="Skills" subtitle="Section 3" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-skills">
      {!editing ? (
        <div>
          <View label="Overall years of QA experience" value={`${u.years_experience || 0} years`}/>
          <div className="mt-3">
            <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2">Testing skills</div>
            {(u.testing_skills || []).length === 0 ? <Empty>No skills added yet. Add skills to get matched with more tests.</Empty> : (
              <div className="flex flex-wrap gap-2">
                {u.testing_skills.map(s => (
                  <Pill key={s} testid={`skill-${s}`}>{s}{u.skill_years?.[s] != null ? <span className="text-[10px] text-slate-500 ml-1">· {u.skill_years[s]}y</span> : null}</Pill>
                ))}
              </div>
            )}
          </div>
          {u.custom_skills && <View className="mt-3" label="Other skills" value={u.custom_skills}/>}
        </div>
      ) : (
        <div className="space-y-4">
          <div><Label>Overall years of experience</Label><Input type="number" min={0} max={40} value={draft.years_experience || 0} onChange={e=>setDraft(d=>({...d, years_experience: e.target.value}))} inputMode="numeric" className="no-spinner" data-testid="input-years"/></div>
          <div>
            <Label>Testing skills</Label>
            <MultiToggle value={draft.testing_skills || []} options={SKILLS} onChange={(v)=>{
              setDraft(d=>{
                const next = { ...d, testing_skills: v };
                const sy = { ...(d.skill_years || {}) };
                Object.keys(sy).forEach(k => { if (!v.includes(k)) delete sy[k]; });
                next.skill_years = sy;
                return next;
              });
            }} testid="toggle-skills"/>
            {(draft.testing_skills || []).length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-[11px] uppercase text-slate-500 tracking-wider">Years per skill</div>
                {draft.testing_skills.map(s => (
                  <div key={s} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-900 w-40 truncate">{s}</span>
                    <Input type="number" min={0} max={40} value={draft.skill_years?.[s] ?? 0}
                      onChange={e=>setDraft(d=>({...d, skill_years: { ...(d.skill_years||{}), [s]: Number(e.target.value) }}))}
                      inputMode="numeric"
                      className="w-24 no-spinner" data-testid={`years-${s}`}/>
                    <span className="text-xs text-slate-500">years</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div><Label>Other skills (comma separated)</Label><Input value={draft.custom_skills || ""} onChange={e=>setDraft(d=>({...d, custom_skills: e.target.value}))} data-testid="input-custom-skills"/></div>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- 4. Devices ---------- */
function DevicesCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, busy }) {
  const hasLegacy =
    (u.device_types || []).length > 0 ||
    (u.device_manufacturers || []).length > 0 ||
    (u.os_versions || []).length > 0 ||
    (u.additional_devices || []).length > 0;
  return (
    <SectionCard icon={Cpu} title="Devices" subtitle="Section 4" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-devices">
      {!editing ? (
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2">My devices</div>
            <DeviceInventoryList
              value={u.device_inventory || []}
              emptyText="No devices added yet. Click Edit to add the devices you can test on."
              testid="profile-device-inventory"
            />
          </div>
          {hasLegacy && (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <div className="text-[11px] uppercase text-slate-500 tracking-wider">Legacy device info (from earlier signup)</div>
              <ListView label="Device types" items={u.device_types}/>
              <ListView label="Manufacturers" items={u.device_manufacturers}/>
              <ListView label="OS versions" items={u.os_versions}/>
              <ListView label="Additional devices" items={u.additional_devices}/>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label>My devices</Label>
            <div className="mt-1.5">
              <DeviceInventory
                value={draft.device_inventory || []}
                onChange={(list)=>setDraft(d=>({...d, device_inventory: list}))}
              />
            </div>
          </div>
          {hasLegacy && (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <div className="text-[11px] uppercase text-slate-500 tracking-wider">Legacy device info</div>
              <div><Label>Device types</Label><MultiToggle value={draft.device_types || []} options={DEVICE_TYPES} onChange={v=>setDraft(d=>({...d, device_types: v}))} testid="toggle-device-types"/></div>
              <div><Label>Manufacturers</Label><MultiToggle value={draft.device_manufacturers || []} options={MANUFACTURERS} onChange={v=>setDraft(d=>({...d, device_manufacturers: v}))} testid="toggle-manufacturers"/></div>
              <div><Label>OS versions</Label><MultiToggle value={draft.os_versions || []} options={OS_VERSIONS} onChange={v=>setDraft(d=>({...d, os_versions: v}))} testid="toggle-os"/></div>
              <div><Label>Additional devices (TVs, wearables, IoT)</Label><MultiToggle value={draft.additional_devices || []} options={ADDITIONAL_DEVICES} onChange={v=>setDraft(d=>({...d, additional_devices: v}))} testid="toggle-additional-devices"/></div>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- 5. Tools ---------- */
function ToolsCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, busy }) {
  return (
    <SectionCard icon={Wrench} title="Tools" subtitle="Section 5" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-tools">
      {!editing ? (
        <div className="space-y-3">
          <ListView label="Testing tools" items={u.tools}/>
          {u.custom_tools && <View label="Other tools" value={u.custom_tools}/>}
          {!u.tools?.length && !u.custom_tools && <Empty>No tools added yet. Add some to broaden your project matches.</Empty>}
        </div>
      ) : (
        <div className="space-y-3">
          <div><Label>Tools</Label><MultiToggle value={draft.tools || []} options={TOOLS} onChange={v=>setDraft(d=>({...d, tools: v}))} testid="toggle-tools"/></div>
          <div><Label>Other tools (comma separated)</Label><Input value={draft.custom_tools || ""} onChange={e=>setDraft(d=>({...d, custom_tools: e.target.value}))} data-testid="input-custom-tools"/></div>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- 6. Domains ---------- */
function DomainsCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, busy }) {
  return (
    <SectionCard icon={Building2} title="Domains" subtitle="Section 6" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-domains">
      {!editing ? (
        <div className="space-y-3">
          <ListView label="Industry verticals" items={u.domains}/>
          {u.custom_domains && <View label="Other domains" value={u.custom_domains}/>}
          {!u.domains?.length && !u.custom_domains && <Empty>No domains added yet.</Empty>}
        </div>
      ) : (
        <div className="space-y-3">
          <div><Label>Domains</Label><MultiToggle value={draft.domains || []} options={DOMAINS} onChange={v=>setDraft(d=>({...d, domains: v}))} testid="toggle-domains"/></div>
          <div><Label>Other domains (comma separated)</Label><Input value={draft.custom_domains || ""} onChange={e=>setDraft(d=>({...d, custom_domains: e.target.value}))} data-testid="input-custom-domains"/></div>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- 7. Availability ---------- */
function AvailabilityCard({ u, editing, draft, setDraft, onEdit, onCancel, onSave, busy }) {
  const slotFor = (day) => (draft.availability_schedule || []).find(a => a.day === day)?.slot || "Not available";
  const setSlot = (day, slot) => {
    const others = (draft.availability_schedule || []).filter(a => a.day !== day);
    if (slot && slot !== "Not available") others.push({ day, slot });
    setDraft(d=>({...d, availability_schedule: others}));
  };
  const toggleDay = (d) => setDraft(prev=>{
    const has = (prev.working_days || []).includes(d);
    return { ...prev, working_days: has ? prev.working_days.filter(x=>x!==d) : [...(prev.working_days||[]), d] };
  });

  return (
    <SectionCard icon={CalendarClock} title="Availability" subtitle="Section 7" editing={editing} onEdit={onEdit} onCancel={onCancel} onSave={onSave} busy={busy} testid="section-availability">
      {!editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <View label="Hours per week" value={`${u.availability_hours || 0} hrs`}/>
            <View label="Working days" value={(u.working_days || []).length ? u.working_days.join(", ") : <Empty>Not set</Empty>}/>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2">Daily schedule</div>
            {(u.availability_schedule || []).length === 0 ? <Empty>No schedule set yet.</Empty> : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {DAYS.map(d => {
                  const slot = (u.availability_schedule || []).find(a=>a.day===d)?.slot;
                  return <div key={d} className="border border-slate-200 rounded-md px-2 py-1"><b className="text-slate-900">{d}</b> <span className="text-slate-500">· {slot || "—"}</span></div>;
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Hours per week</Label><Input type="number" min={0} max={80} value={draft.availability_hours || 0} onChange={e=>setDraft(d=>({...d, availability_hours: Number(e.target.value)}))} inputMode="numeric" className="no-spinner" data-testid="input-hours"/></div>
          </div>
          <div>
            <Label>Working days</Label>
            <MultiToggle value={draft.working_days || []} options={DAYS} onChange={v=>setDraft(d=>({...d, working_days: v}))} testid="toggle-days"/>
          </div>
          <div>
            <Label>Daily schedule</Label>
            <div className="space-y-2 mt-2" data-testid="schedule-grid">
              {DAYS.map(d => (
                <div key={d} className="flex items-center gap-3">
                  <div className="w-12 text-sm font-medium text-slate-700">{d}</div>
                  <Select value={slotFor(d)} onValueChange={(v)=>setSlot(d, v)}>
                    <SelectTrigger className="flex-1" data-testid={`schedule-${d}`}><SelectValue/></SelectTrigger>
                    <SelectContent>{TIME_SLOTS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- 8. Agreements & Documents (read-only with upload/replace) ---------- */
/* ---------- Side widgets ---------- */
// ProfileSummaryCard & ProfileCompletionCard moved to ./profile/SideWidgets.jsx

/* ---------- Tiny helpers ---------- */
function View({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase text-slate-500 tracking-wider">{label}</div>
      <div className="font-medium text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
function ListView({ label, items }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-1">{label}</div>
      {(items || []).length === 0 ? <Empty>Not added yet.</Empty> : (
        <div className="flex flex-wrap gap-2">{items.map(i => <Pill key={i}>{i}</Pill>)}</div>
      )}
    </div>
  );
}
